import {
  mergeAttributes,
  Node,
  NodeViewWrapper,
  ReactNodeViewRenderer
} from '@tiptap/react';
import { useEffect, useRef } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

export interface Attributes {
  id: string;
  isReadOnly: boolean;
  options: {
    label: string;
    value: string;
  }[];
  placeholder: string;
  value: null | string;
}

const SelectFieldView: Parameters<typeof ReactNodeViewRenderer>[0] = ({
  node,
  selected,
  updateAttributes
}) => {
  const { isReadOnly, options, placeholder, value } = node.attrs as Attributes;

  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!selected || !triggerRef.current) return;

    triggerRef.current.focus();
  }, [selected]);

  const handleValueChange = (value: string) => {
    updateAttributes({ value });
  };

  return (
    <NodeViewWrapper
      as="span"
      className="inline-block align-middle"
    >
      <Select
        disabled={isReadOnly}
        onValueChange={handleValueChange}
        value={value ?? undefined}
      >
        <SelectTrigger
          className="h-8! rounded-sm px-2"
          ref={triggerRef}
          tabIndex={-1}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem
              key={option.value}
              value={option.value}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </NodeViewWrapper>
  );
};

const SelectField = Node.create({
  addAttributes() {
    return {
      id: {
        default: '',
        parseHTML: element => element.getAttribute('data-id'),
        renderHTML: (attrs: Attributes) => ({ 'data-id': attrs.id })
      },
      isReadOnly: {
        default: false,
        parseHTML: element => element.getAttribute('data-is-read-only'),
        renderHTML: (attrs: Attributes) => ({
          'data-is-read-only': attrs.isReadOnly
        })
      },
      options: {
        default: [],
        parseHTML: element => {
          const json = element.getAttribute('data-options');
          try {
            return json ? (JSON.parse(json) as Attributes['options']) : [];
          } catch {
            return [];
          }
        },
        renderHTML: (attrs: Attributes) => ({
          'data-options': JSON.stringify(attrs.options)
        })
      },
      placeholder: {
        default: null,
        parseHTML: element => element.getAttribute('data-placeholder'),
        renderHTML: (attrs: Attributes) => ({
          'data-placeholder': attrs.placeholder
        })
      },
      value: {
        default: null,
        parseHTML: element => element.getAttribute('data-value'),
        renderHTML: (attrs: Attributes) => ({
          'data-value': attrs.value ?? ''
        })
      }
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(SelectFieldView);
  },
  atom: true,
  group: 'inline',
  inline: true,
  name: 'selectField',
  parseHTML() {
    return [{ tag: 'span[data-select-field]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-select-field': '' }),
      0
    ];
  }
});

export default SelectField;
